import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Factory from "@mui/icons-material/Factory";
import {
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import CopyableTypography from "components/Typography/CopyableTypography";
import { useLocalStorageState } from "hooks/useLocalStorageState";

import { FieldDisplay } from "./shared/FieldComponents";
import { useFulfillmentDetails } from "./useFulfillmentDetails";

// Custom hook for facility actions (separating business logic from UI)
const useFacilityActions = () => {
  const { data } = useFulfillmentDetails();

  const openFacilityProfile = () => {
    if (data?.tierIIReport) {
      window.open(
        `/o/${data.tierIIReport.tenantId}/facilities/${data.tierIIReport.facility.id}/profile`,
        "_blank"
      );
    }
  };

  return { openFacilityProfile };
};

const FacilityInfoSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const AlternateIdsSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

export function Facility() {
  const theme = useTheme();
  const { data } = useFulfillmentDetails();
  const { openFacilityProfile } = useFacilityActions();
  const [isExpanded, setIsExpanded] = useLocalStorageState<boolean>(
    "fulfillment-facility-expanded",
    { defaultValue: true }
  );

  const getFullMailingAddress = () => {
    const address1 = data?.tierIIReport.facility.mailingStreetAddress1 || "";
    const address2 = data?.tierIIReport.facility.mailingStreetAddress2 || "";
    const city = data?.tierIIReport.facility.mailingCity || "";
    const state = data?.tierIIReport.facility.mailingState || "";
    const zip = data?.tierIIReport.facility.mailingZip || "";

    const parts = [address1, address2, city, state, zip].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "N/A";
  };

  // Always visible fields
  const alwaysVisibleFields = (
    <FacilityInfoSection>
      <FieldDisplay
        label="Organization"
        value={
          <CopyableTypography
            text={
              data?.tierIIReport.tenant?.name || data?.tierIIReport.facility.tenant?.name || "N/A"
            }
          />
        }
      />
      <FieldDisplay
        label="Facility Name"
        value={
          <CopyableTypography
            text={data?.tierIIReport.facility.name || "N/A"}
            sx={{ fontWeight: "bold" }}
          />
        }
      />
      <FieldDisplay
        label="Mailing Address"
        value={<CopyableTypography text={getFullMailingAddress()} />}
      />
    </FacilityInfoSection>
  );

  // Additional fields that can be collapsed
  const additionalFacilityInfo = (
    <FacilityInfoSection>
      <FieldDisplay
        label="Company Name"
        value={<CopyableTypography text={data?.tierIIReport.facility.companyName || "N/A"} />}
      />
      <FieldDisplay
        label="NAICS Code"
        value={
          <CopyableTypography text={data?.tierIIReport.facility.naicsCode?.toString() || "N/A"} />
        }
      />
      <FieldDisplay
        label="Address"
        value={
          <CopyableTypography
            text={
              `${data?.tierIIReport.facility.streetAddress1 ?? ""}${
                data?.tierIIReport.facility.streetAddress2
                  ? `, ${data?.tierIIReport.facility.streetAddress2}`
                  : ""
              }` || "N/A"
            }
          />
        }
      />
      <FieldDisplay
        label="City"
        value={<CopyableTypography text={data?.tierIIReport.facility.city || "N/A"} />}
      />
      <FieldDisplay
        label="State"
        value={<CopyableTypography text={data?.tierIIReport.facility.state || "N/A"} />}
      />
      <FieldDisplay
        label="Zip"
        value={<CopyableTypography text={data?.tierIIReport.facility.zip || "N/A"} />}
      />
      <FieldDisplay
        label="County"
        value={<CopyableTypography text={data?.tierIIReport.facility.county || "N/A"} />}
      />
    </FacilityInfoSection>
  );

  const alternateIds = data?.tierIIReport.facility.facilityAlternateIds &&
    data?.tierIIReport.facility.facilityAlternateIds.length > 0 && (
      <AlternateIdsSection>
        <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
          Facility Alternate IDs
        </Typography>
        {data.tierIIReport.facility.facilityAlternateIds.map(({ id, type, value }: any) => (
          <FieldDisplay key={id} label={type} value={<CopyableTypography text={value} />} />
        ))}
      </AlternateIdsSection>
    );

  const headerButton = (
    <Button
      size="small"
      startIcon={<Factory sx={{ fontSize: 18 }} />}
      onClick={openFacilityProfile}
    >
      Open Facility
    </Button>
  );

  return (
    <Stack
      direction="column"
      component={Paper}
      elevation={0}
      spacing={2}
      padding={theme.spacing(2, 3, 2, 3)}
    >
      {/* Header with title and action button */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Typography variant="h6">Facility</Typography>
          <IconButton size="small" sx={{ ml: 1 }}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        {headerButton}
      </Stack>

      {/* Always visible fields */}
      {alwaysVisibleFields}

      <Collapse in={isExpanded}>
        <Grid container spacing={3}>
          {/* Additional facility info */}
          <Grid size={12}>{additionalFacilityInfo}</Grid>

          {/* Alternate IDs */}
          <Grid size={12}>{alternateIds}</Grid>
        </Grid>
      </Collapse>
    </Stack>
  );
}
